import { useI18n } from '../../../i18n';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, Legend,
  ResponsiveContainer, CartesianGrid,
} from 'recharts';
import { cn } from '../../../utils/cn';

export interface DailyAccessDatum {
  day: string;
  open_count: number;
  denied_count: number;
}

interface Props {
  data: DailyAccessDatum[];
  title?: string;
  className?: string;
}

interface TooltipProps {
  active?: boolean;
  payload?: Array<{ value: number; name: string; color: string }>;
  label?: string;
}

function CustomTooltip({ active, payload, label }: TooltipProps) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg bg-white px-3 py-2 shadow-lg ring-1 ring-black/5 text-xs">
      <p className="font-medium text-[#191B23]">{label}</p>
      {payload.map((p) => (
        <p key={p.name} style={{ color: p.color }}>
          {p.name}: {p.value}
        </p>
      ))}
    </div>
  );
}

export function DailyAccessChart({ data, title = 'Door openings over time', className }: Props) {
  const { t } = useI18n();
  return (
    <div className={cn('rounded-2xl bg-white p-6 shadow-sm border border-slate-100', className)}>
      <h3 className="mb-4 text-base font-bold text-[#191B23]">{title}</h3>

      {data.length === 0 ? (
        <div className="flex h-[260px] items-center justify-center text-sm text-slate-500">
          {t('noStatsDataInPeriod')}
        </div>
      ) : (
        <div className="h-[260px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data} margin={{ top: 5, right: 5, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" vertical={false} />
              <XAxis
                dataKey="day"
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 10, fill: '#94A3B8' }}
              />
              <YAxis
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 10, fill: '#94A3B8' }}
                width={30}
                allowDecimals={false}
              />
              <Tooltip content={<CustomTooltip />} cursor={{ fill: '#F8FAFC' }} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="open_count" name={t('atworkOpenings')} fill="#22c55e" radius={[4, 4, 0, 0]} />
              <Bar dataKey="denied_count" name={t('atworkDenied')} fill="#ef4444" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}
